import React, { ReactNode } from 'react';
import { AddOnItem } from '~/types/models/addon';
import { PlanUsage } from '~/types/models';
import { Box } from '../../Common/Box';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import useDate from '~/react-hooks/use-date';
import { HorizontalDivider } from '../../Common/HorizontalDivider';
import { UsagesPanel } from './UsagesPanel';
import { AddOnsPanel } from './AddOnsPanel';
import { Label } from './Label';
import { PricePanel } from './PricePanel';

export interface BillingProductCardProps {
  label: ReactNode;
  basicPrice?: ReactNode;
  totalPrice?: ReactNode;
  secondaryCta?: ReactNode;
  progressComponent?: ReactNode;
  progress?: number;
  maxProgress?: number;
  addOns?: AddOnItem[];
  usages?: PlanUsage[];
  billingDate?: string | null;
  discountFormat?: string;
  onManageClick?: () => void;
  hideAddOns?: boolean;
  hideBillingDate?: boolean;
  hideCta?: boolean;
  hideTotalPrice?: boolean;
  hidePrice?: boolean;
}

export function BillingProductCard({
  label,
  progressComponent,
  addOns,
  usages,
  basicPrice,
  billingDate,
  totalPrice,
  discountFormat,
  onManageClick,
  secondaryCta,
  hideAddOns = false,
  hideBillingDate = false,
  hideCta = false,
  hideTotalPrice = false,
  hidePrice = false,
}: BillingProductCardProps) {
  const billingDateTime = useDate(billingDate);
  return (
    <Box className='w-full flex flex-col w-full'>
      <div className='w-full flex flex-col xmd:flex-row gap-y-4 gap-x-2'>
        <div className='w-full xmd:w-2/5 flex flex-col gap-y-1.5'>
          <Label>Product</Label>
          <span className='text-[21px] font-bold'>{label}</span>
          {progressComponent}
        </div>
        <div className='w-full xmd:w-3/5 flex flex-col gap-y-6'>
          <div className='flex flex-row justify-between'>
            <UsagesPanel usages={usages} />
            {!hidePrice && (
              <PricePanel
                discountLabel={discountFormat}
                priceLabel={basicPrice}
              />
            )}
          </div>
          {!hideAddOns && <AddOnsPanel addOns={addOns} />}
        </div>
      </div>
      {(!hideBillingDate || !hideCta || !hideTotalPrice) && (
        <>
          <HorizontalDivider />
          <div className='w-full flex flex-col xmd:flex-row items-center gap-y-4 gap-x-4'>
            <div className='w-full xmd:w-2/5 flex flex-row gap-x-4 items-end xmd:items-start'>
              {!hideCta && (
                <>
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={onManageClick}
                    className='py-2 w-full xmd:w-auto v2-text-neutral-900'
                  >
                    Manage subscription
                  </Button>
                  {secondaryCta}
                </>
              )}
            </div>
            <div className='w-full xmd:w-2/5 flex justify-end xmd:justify-start'>
              {!hideBillingDate && billingDate && (
                <div className='flex flex-row gap-x-8 items-center flex-wrap v2-text-neutral-900'>
                  <span className='text-base font-bold '>
                    Next billing date
                  </span>
                  <span className='text-[21px]'>
                    {billingDate ? billingDateTime.local?.toFormat('DD') : '-'}
                  </span>
                </div>
              )}
            </div>
            <div className='w-full xmd:w-1/5 flex flex-row justify-end'>
              {!hideTotalPrice && (
                <div className='flex flex-row gap-x-8 flex-wrap items-center v2-text-neutral-900'>
                  <span className='text-base font-bold'>Total</span>
                  <span className='text-[28px]'>
                    {totalPrice ? totalPrice : '$0'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Box>
  );
}
